import api from './api.service';

// APM Service Discovery interfaces
export interface APMService {
  id: string;
  tenant_id: string;
  application_id: string;
  service_name: string;
  service_version?: string;
  language?: string;
  framework?: string;
  environment?: string;
  first_seen: Date;
  last_seen: Date;
  total_requests: number;
  error_count: number;
  avg_duration_ms: number;
  p95_duration_ms?: number;
  p99_duration_ms?: number;
  metadata?: Record<string, any>;
  created_at: Date;
  updated_at: Date;
}

export interface APMServiceDependency {
  id: string;
  tenant_id: string;
  from_service_id: string;
  to_service_id: string;
  dependency_type: string; // 'http', 'grpc', 'database', 'message_queue', etc
  total_calls: number;
  error_count: number;
  avg_duration_ms: number;
  last_called: Date;
  created_at: Date;
  updated_at: Date;
  fromService?: APMService;
  toService?: APMService;
}

// Legacy interface for backward compatibility
export interface Service {
  id: string;
  tenant_id: string;
  application_id: string;
  name: string;
  type: 'web' | 'api' | 'database' | 'cache' | 'queue' | 'external';
  language?: string;
  version?: string;
  environment?: string;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  position_x?: number;
  position_y?: number;
  metadata?: any;
  last_seen_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ServiceConnection {
  id: string;
  tenant_id: string;
  source_service_id: string;
  target_service_id: string;
  protocol?: string;
  calls_per_minute: number;
  avg_latency_ms?: number;
  error_rate: number;
  last_call_at?: string;
  created_at: string;
  updated_at: string;
  source_service?: Service;
  target_service?: Service;
}

export interface ServiceMap {
  services: Service[];
  connections: ServiceConnection[];
}

export const serviceMapApi = {
  // New APM Service Discovery endpoints
  getAPMServices: async (applicationId?: string, includeInactive = false): Promise<{ success: boolean; data: APMService[] }> => {
    const params: any = { includeInactive };
    if (applicationId && applicationId !== 'all') {
      params.applicationId = applicationId;
    }
    return api.get('/v1/apm/services', { params });
  },

  getAPMDependencies: async (applicationId?: string): Promise<{ success: boolean; data: APMServiceDependency[] }> => {
    const params: any = {};
    if (applicationId && applicationId !== 'all') {
      params.applicationId = applicationId;
    }
    return api.get('/v1/apm/services/dependencies', { params });
  },

  // Combined method for service map
  getServiceMap: async (applicationId?: string): Promise<ServiceMap> => {
    const [servicesRes, dependenciesRes] = await Promise.all([
      serviceMapApi.getAPMServices(applicationId),
      serviceMapApi.getAPMDependencies(applicationId),
    ]);

    // Convert APM services to legacy Service format
    const services: Service[] = servicesRes.data.map((apmService) => {
      // Calculate error rate as percentage
      const errorRate = apmService.total_requests > 0
        ? (apmService.error_count / apmService.total_requests) * 100
        : 0;

      // We don't have time-bucketed data, so we can't calculate real req/min
      // Just pass the total_requests as-is, the frontend will display it appropriately

      return {
        id: apmService.id,
        tenant_id: apmService.tenant_id,
        application_id: apmService.application_id,
        name: apmService.service_name,
        type: determineServiceType(apmService),
        language: apmService.language,
        version: apmService.service_version,
        environment: apmService.environment,
        status: determineServiceStatus(apmService),
        metadata: {
          ...apmService.metadata,
          total_requests: apmService.total_requests,
          error_count: apmService.error_count,
          error_rate: errorRate,
          avg_latency_ms: apmService.avg_duration_ms,
          p95_latency_ms: apmService.p95_duration_ms,
          p99_latency_ms: apmService.p99_duration_ms,
          first_seen: apmService.first_seen,
        },
        last_seen_at: new Date(apmService.last_seen).toISOString(),
        created_at: new Date(apmService.created_at).toISOString(),
        updated_at: new Date(apmService.updated_at).toISOString(),
      };
    });

    // Convert APM dependencies to legacy ServiceConnection format
    const connections: ServiceConnection[] = dependenciesRes.data.map((dep) => {
      // Calculate calls per minute based on time window
      const createdAt = new Date(dep.created_at).getTime();
      const lastCalled = new Date(dep.last_called).getTime();
      const durationMinutes = Math.max(1, (lastCalled - createdAt) / 60000);
      const callsPerMinute = dep.total_calls > 0
        ? Math.round(dep.total_calls / durationMinutes)
        : 0;

      return {
        id: dep.id,
        tenant_id: dep.tenant_id,
        source_service_id: dep.from_service_id,
        target_service_id: dep.to_service_id,
        protocol: dep.dependency_type,
        calls_per_minute: callsPerMinute,
        avg_latency_ms: dep.avg_duration_ms,
        error_rate: dep.total_calls > 0 ? (dep.error_count / dep.total_calls) * 100 : 0,
        last_call_at: new Date(dep.last_called).toISOString(),
        created_at: new Date(dep.created_at).toISOString(),
        updated_at: new Date(dep.updated_at).toISOString(),
      };
    });

    return { services, connections };
  },

  getServices: async (applicationId?: string): Promise<Service[]> => {
    const response = await serviceMapApi.getAPMServices(applicationId);
    return response.data.map((apmService) => ({
      id: apmService.id,
      tenant_id: apmService.tenant_id,
      application_id: apmService.application_id,
      name: apmService.service_name,
      type: determineServiceType(apmService),
      language: apmService.language,
      version: apmService.service_version,
      environment: apmService.environment,
      status: determineServiceStatus(apmService),
      metadata: apmService.metadata,
      last_seen_at: new Date(apmService.last_seen).toISOString(),
      created_at: new Date(apmService.created_at).toISOString(),
      updated_at: new Date(apmService.updated_at).toISOString(),
    }));
  },

  getConnections: async (): Promise<ServiceConnection[]> => {
    const response = await serviceMapApi.getAPMDependencies();
    return response.data.map((dep) => ({
      id: dep.id,
      tenant_id: dep.tenant_id,
      source_service_id: dep.from_service_id,
      target_service_id: dep.to_service_id,
      protocol: dep.dependency_type,
      calls_per_minute: Math.round(dep.total_calls / 60),
      avg_latency_ms: dep.avg_duration_ms,
      error_rate: dep.total_calls > 0 ? (dep.error_count / dep.total_calls) * 100 : 0,
      last_call_at: new Date(dep.last_called).toISOString(),
      created_at: new Date(dep.created_at).toISOString(),
      updated_at: new Date(dep.updated_at).toISOString(),
    }));
  },

  // Legacy manual service management (deprecated)
  createService: (data: Partial<Service>): Promise<Service> => {
    return api.post('/v1/service-map/services', data);
  },

  updateService: (id: string, data: Partial<Service>): Promise<Service> => {
    return api.put(`/v1/service-map/services/${id}`, data);
  },

  updateServicePosition: (id: string, x: number, y: number): Promise<void> => {
    return api.patch(`/v1/service-map/services/${id}/position`, { x, y });
  },

  deleteService: (id: string): Promise<void> => {
    return api.delete(`/v1/service-map/services/${id}`);
  },
};

// Helper functions
function determineServiceType(service: APMService): 'web' | 'api' | 'database' | 'cache' | 'queue' | 'external' {
  const name = service.service_name.toLowerCase();
  const framework = service.framework?.toLowerCase() || '';

  if (name.includes('db') || name.includes('database') || name.includes('mysql') || name.includes('postgres')) {
    return 'database';
  }
  if (name.includes('redis') || name.includes('cache') || name.includes('memcache')) {
    return 'cache';
  }
  if (name.includes('queue') || name.includes('kafka') || name.includes('rabbit') || name.includes('sqs')) {
    return 'queue';
  }
  if (framework.includes('spring') || framework.includes('express') || framework.includes('fastapi')) {
    return 'api';
  }
  if (name.includes('frontend') || name.includes('web') || framework.includes('react') || framework.includes('vue')) {
    return 'web';
  }

  return 'api'; // Default
}

function determineServiceStatus(service: APMService): 'healthy' | 'warning' | 'critical' | 'unknown' {
  if (service.total_requests === 0) {
    return 'unknown';
  }

  const errorRate = (service.error_count / service.total_requests) * 100;

  // Check if service was seen recently (last 5 minutes)
  const lastSeenDate = new Date(service.last_seen);
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

  if (lastSeenDate < fiveMinutesAgo) {
    return 'critical'; // Service hasn't reported in 5 minutes
  }

  if (errorRate > 10) {
    return 'critical';
  }
  if (errorRate > 5) {
    return 'warning';
  }

  return 'healthy';
}
